import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfigurationPageHeader } from '@modules/configuration/components/ConfigurationPageHeader';
import {
  configurationPageClass,
  configurationPageContentClass,
} from '@modules/configuration/components/configurationLayout';
import { RolesModel } from '../../models/Roles';
import { CreateRoleButton, FiltersSection } from '../interaction';
import { RolesEmptyState } from './RolesEmptyState';
import { RolesList } from './RolesList';

interface RolesViewProps {
  roles: RolesModel[];
  totalCount: number;
  searchTerm: string;
  isFetching?: boolean;
  isFiltering?: boolean;
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onSearchChange: (value: string) => void;
  onEdit: (role: RolesModel) => void;
  onDelete: (role: RolesModel) => void;
  onRefetch: () => void;
}

export function RolesView({
  roles,
  totalCount,
  searchTerm,
  isFetching = false,
  isFiltering = false,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  onSearchChange,
  onEdit,
  onDelete,
  onRefetch,
}: RolesViewProps) {
  const { t } = useTranslation('rolesModule');
  const [searchInitialValue] = useState(() => searchTerm);

  const hasRoles = totalCount > 0;
  const hasFilteredResults = roles.length > 0;
  const isSearchWithNoResults = hasRoles && !hasFilteredResults && searchTerm.trim().length > 0;
  const showingCount = roles.length;
  const isFiltered = searchTerm.trim().length > 0;
  const isBusy = isFetching || isFiltering;

  return (
    <div
      className={`${configurationPageClass} pb-6 transition-opacity duration-300 ${isBusy ? 'pointer-events-none opacity-90' : ''}`}
      data-testid="roles-view"
    >
      <div className="shrink-0">
        <ConfigurationPageHeader
          title={t('pageTitle')}
          description={t('pageDescription')}
          action={hasRoles ? <CreateRoleButton onRoleCreated={onRefetch} /> : undefined}
        />

        {hasRoles && (
          <div className="mt-8 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            <FiltersSection initialValue={searchInitialValue} onSearchChange={onSearchChange} />
            <p className="shrink-0 text-xs text-muted-foreground sm:text-sm">
              {isFiltered
                ? t('list.countFiltered', { count: showingCount, total: totalCount })
                : t('list.count', { count: totalCount })}
            </p>
          </div>
        )}
      </div>

      <div className={`${configurationPageContentClass} flex flex-col overflow-hidden`}>
        {!hasRoles ? (
          <RolesEmptyState onRoleCreated={onRefetch} />
        ) : isSearchWithNoResults ? (
          <div
            className="rounded-lg border border-dashed border-border bg-muted/20 px-6 py-10 text-center"
            data-testid="roles-no-results"
          >
            <p className="text-sm font-medium text-foreground">{t('emptyState.noResultsTitle')}</p>
            <p className="mt-1 text-sm text-muted-foreground">{t('emptyState.noResults')}</p>
          </div>
        ) : (
          <RolesList
            roles={roles}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        )}
      </div>
    </div>
  );
}
